<template>
  <div
    v-if="tip"
    class="relative flex items-center gap-3 rounded border p-4"
    :class="{
      'border-yellow-200 bg-yellow-50': tip.type === 'warning',
      'border-green-3 bg-green-4': tip.type === 'success',
    }"
  >
    <div class="flex-shrink-0">
      <v-icon
        size="18"
        :class="{
          'text-yellow-500': tip.type === 'warning',
          'text-green-2': tip.type === 'success',
        }"
      >
        {{ tip.type === "warning" ? "fa:far fa-lightbulb" : "fa:far fa-check-circle" }}
      </v-icon>
    </div>
    <div class="flex-1">
      <p
        class="text-sm"
        :class="{
          'text-yellow-600': tip.type === 'warning',
          'text-green-50': tip.type === 'success',
        }"
      >
        {{ tip.text }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import StatusesConstants from "@/utils/constants/PromptStatusesConstants";
import { DEFAULT_TITLE_MESSAGE_ACTION } from "@/utils/constants/PromptActionsConstants";
import { useAppsStore } from "@/pinia/appsStore";
import TimezonesConstants from "@/utils/constants/TimezonesConstants";
import dayjs from "dayjs";

const props = defineProps({
  prompt: { type: Object, required: true },
  promptStatus: { type: Object, required: true },
});

const appsStore = useAppsStore();
const timezone = computed(() => {
  const offset = appsStore.app?.timezone_offset || "Pacific Time (US & Canada)";
  return TimezonesConstants[offset];
});

const estimatedTime = computed(() => {
  if (!props.prompt.start_date) return "";

  const startDate = dayjs.tz(props.prompt.start_date, timezone.value);
  const duration = dayjs.duration(startDate.diff(dayjs()));
  const days = Math.round(duration.asDays());
  const hours = Math.round(duration.asHours());
  const minutes = Math.round(duration.asMinutes());

  if (days >= 7) return `on ${startDate.format("MMM Do YYYY, h:mm a")}`;
  if (days >= 1) return `in ${days} day${days === 1 ? "" : "s"}`;
  if (hours >= 1) return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  if (minutes >= 1) return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;

  return "in a minute";
});

const tip = computed(() => {
  const type = props.promptStatus?.type;
  const isPending = type === StatusesConstants.pending.type;
  const isScheduled = type === StatusesConstants.ready.type;

  const { rf_retention_title, rf_retention_message, rf_push_action } = props.prompt.actions;

  // Draft / Scheduled
  if (isPending || isScheduled) {
    const preamble = isScheduled ? "For this prompt to be run" : "To run or schedule this prompt";

    // No segments assigned
    if (!props.prompt.segments?.length) {
      return {
        type: "warning",
        text: `${preamble}, add a segment in the "Details" section below`,
      };
    }

    // Default or missing title, message, action
    if (
      !rf_retention_title ||
      !rf_retention_message ||
      !rf_push_action ||
      rf_retention_title?.match(DEFAULT_TITLE_MESSAGE_ACTION) ||
      rf_retention_message?.match(DEFAULT_TITLE_MESSAGE_ACTION)
    ) {
      return {
        type: "warning",
        text: `${preamble}, click "Edit prompt design" and set a notification title, body, and action`,
      };
    }

    return {
      type: "success",
      text: isScheduled
        ? `Prompt is scheduled to run ${estimatedTime.value}`
        : "Prompt is ready to be run",
    };
  }

  return null;
});
</script>
